import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  Timestamp,
  collectionGroup,
  QueryConstraint,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import {
  AdminUser,
  AdminOrganization,
  AdminSession,
  AdminReport,
  AdminStats,
  SearchFilters,
  AssessorInfo,
  ConsultantInfo
} from '../types/admin.types';
import { CalculatorFormData, CalculatorResults } from '../../calculator/types';

// Helper function to determine if email is from Rooster
const isRoosterEmail = (email: string): boolean => {
  return email.toLowerCase().includes('@rooster') || 
         email.toLowerCase().includes('@getrooster');
};

// Helper function to get session freshness
export const getSessionFreshness = (lastActivity: Date | Timestamp): 'fresh' | 'stale' | 'old' => {
  const activityDate = lastActivity instanceof Timestamp ? lastActivity.toDate() : lastActivity;
  const daysSince = (Date.now() - activityDate.getTime()) / (1000 * 60 * 60 * 24);
  
  if (daysSince < 7) return 'fresh';
  if (daysSince < 30) return 'stale';
  return 'old';
};

// Helper function to get days since activity
export const getDaysSinceActivity = (lastActivity: Date | Timestamp): number => {
  const activityDate = lastActivity instanceof Timestamp ? lastActivity.toDate() : lastActivity;
  return Math.floor((Date.now() - activityDate.getTime()) / (1000 * 60 * 60 * 24));
};

// Fetch all users with optional filters
export const fetchPersons = async (filters?: SearchFilters): Promise<AdminUser[]> => {
  try {
    const usersRef = collection(db, 'users');
    const constraints: QueryConstraint[] = [];

    if (filters?.searchTerm) {
      // Note: Firestore doesn't support full-text search, so we'll filter in memory
      constraints.push(orderBy('profile.lastActive', 'desc'));
    } else {
      constraints.push(orderBy('profile.lastActive', 'desc'));
    }

    if (filters?.dateFrom || filters?.dateTo) {
      // We'll filter by date in memory after fetching
    }

    const q = query(usersRef, ...constraints);
    const snapshot = await getDocs(q);
    
    // Also get all sessions using collectionGroup to count them properly
    const allSessionsRef = collectionGroup(db, 'calculator_sessions');
    const allSessionsSnapshot = await getDocs(allSessionsRef);
    
    // Create a map of email -> session count
    const emailSessionCount = new Map<string, number>();
    const emailOrganizations = new Map<string, Set<string>>();
    
    allSessionsSnapshot.forEach(sessionDoc => {
      const sessionData = sessionDoc.data();
      const email = sessionData.formData?.contactEmail || sessionData.contactInfo?.contactEmail || '';
      const orgName = sessionData.formData?.organizationName || sessionData.contactInfo?.organizationName;
      
      if (email) {
        emailSessionCount.set(email, (emailSessionCount.get(email) || 0) + 1);
        
        if (orgName) {
          if (!emailOrganizations.has(email)) {
            emailOrganizations.set(email, new Set<string>());
          }
          emailOrganizations.get(email)!.add(orgName);
        }
      }
    });
    
    const users: AdminUser[] = [];
    const processedEmails = new Set<string>();
    
    // First, add users from the users collection
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const profile = data.profile || {};
      const userEmail = profile.contactEmail || '';
      
      if (userEmail) {
        processedEmails.add(userEmail);
      }
      
      const user: AdminUser = {
        userId: docSnap.id,
        email: profile.contactEmail || '',
        name: profile.contactName || '',
        organizationName: profile.organizationName || '',
        title: profile.contactTitle,
        phone: profile.contactPhone,
        isRooster: isRoosterEmail(profile.contactEmail || ''),
        firstSeen: profile.firstSeen || Timestamp.now(),
        lastActive: profile.lastActive || Timestamp.now(),
        totalSessions: emailSessionCount.get(userEmail) || 0,
        organizationsAssessed: Array.from(emailOrganizations.get(userEmail) || new Set())
      };
      
      // Apply filters
      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        if (!user.email.toLowerCase().includes(searchLower) &&
            !user.name.toLowerCase().includes(searchLower) &&
            !user.organizationName.toLowerCase().includes(searchLower)) {
          continue;
        }
      }
      
      if (filters?.isRooster !== undefined) {
        if (user.isRooster !== filters.isRooster) {
          continue;
        }
      }
      
      users.push(user);
    }
    
    // Now, add users from sessions who don't have user documents
    for (const [email, sessionCount] of Array.from(emailSessionCount.entries())) {
      if (!processedEmails.has(email)) {
        
        // Get the first session for this email to extract user details
        const userSession = allSessionsSnapshot.docs.find(doc => {
          const data = doc.data();
          const sessionEmail = data.formData?.contactEmail || data.contactInfo?.contactEmail || '';
          return sessionEmail === email;
        });
        
        if (userSession) {
          const sessionData = userSession.data();
          const formData = sessionData.formData || {};
          const contactInfo = sessionData.contactInfo || {};
          
          const user: AdminUser = {
            userId: `session-user-${email.replace(/[@.]/g, '_')}`, // Generate a unique ID
            email: email,
            name: formData.contactName || contactInfo.contactName || '',
            organizationName: formData.organizationName || contactInfo.organizationName || '',
            title: formData.contactTitle || contactInfo.contactTitle,
            phone: formData.contactPhone || contactInfo.contactPhone,
            isRooster: isRoosterEmail(email),
            firstSeen: sessionData.startedAt || sessionData.createdAt || Timestamp.now(),
            lastActive: sessionData.completedAt || sessionData.updatedAt || sessionData.startedAt || Timestamp.now(),
            totalSessions: sessionCount,
            organizationsAssessed: Array.from(emailOrganizations.get(email) || new Set())
          };
          
          // Apply filters
          if (filters?.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            if (!user.email.toLowerCase().includes(searchLower) &&
                !user.name.toLowerCase().includes(searchLower) &&
                !user.organizationName.toLowerCase().includes(searchLower)) {
              continue;
            }
          }
          
          if (filters?.isRooster !== undefined) {
            if (user.isRooster !== filters.isRooster) {
              continue;
            }
          }
          
          users.push(user);
        }
      }
    }
    
    return users;
  } catch (error) {
    return [];
  }
};

// Alias for backward compatibility
export const fetchUsers = fetchPersons;

// Fetch organizations with assessments
export const fetchOrganizations = async (filters?: SearchFilters): Promise<AdminOrganization[]> => {
  try {
    // Use collectionGroup to get all sessions across all users
    const sessionsRef = collectionGroup(db, 'calculator_sessions');
    const snapshot = await getDocs(sessionsRef);
    
    // Group sessions by organization
    const orgMap = new Map<string, any[]>();
    
    snapshot.forEach(doc => {
      const data = doc.data();
      const orgName = data.formData?.organizationName || data.contactInfo?.organizationName;
      
      if (orgName) {
        if (!orgMap.has(orgName)) {
          orgMap.set(orgName, []);
        }
        orgMap.get(orgName)!.push(data);
      }
    });
    
    const organizations: AdminOrganization[] = [];
    
    for (const [orgName, sessions] of Array.from(orgMap.entries())) {
      
      // Calculate organization metrics
      const assessorMap = new Map<string, AssessorInfo>();
      let totalTAS = 0;
      let tasCount = 0;
      let bestTAS = 0;
      let firstAssessment = sessions[0].startedAt || sessions[0].createdAt || sessions[0].savedAt;
      let lastAssessment = sessions[0].completedAt || sessions[0].updatedAt || sessions[0].startedAt || sessions[0].createdAt;
      
      sessions.forEach((session: any) => {
        // Track assessors
        const email = session.formData?.contactEmail || session.contactInfo?.contactEmail || '';
        if (email) {
          if (!assessorMap.has(email)) {
            assessorMap.set(email, {
              email,
              name: session.formData?.contactName || session.contactInfo?.contactName || '',
              isRooster: isRoosterEmail(email),
              assessmentCount: 0,
              lastAssessment: session.completedAt || session.startedAt
            });
          }
          const assessor = assessorMap.get(email)!;
          assessor.assessmentCount++;
          if (session.completedAt && session.completedAt > assessor.lastAssessment) {
            assessor.lastAssessment = session.completedAt;
          }
        }
        
        // Only track TAS scores from finalized or completed sessions
        const isFinalized = session.finalizedAt || session.completedAt;
        if (isFinalized) {
          const currentTAS = session.calculationSnapshot?.currentTAS || 
                            session.results?.currentTAS || 
                            session.currentTAS;
          
          if (currentTAS) {
            totalTAS += currentTAS;
            tasCount++;
            if (currentTAS > bestTAS) {
              bestTAS = currentTAS;
            }
          }
        }
        
        // Track dates with proper fallbacks
        const sessionStart = session.startedAt || session.createdAt || session.savedAt;
        const sessionEnd = session.completedAt || session.updatedAt || sessionStart;
        
        if (sessionStart && (!firstAssessment || sessionStart < firstAssessment)) {
          firstAssessment = sessionStart;
        }
        if (sessionEnd && (!lastAssessment || sessionEnd > lastAssessment)) {
          lastAssessment = sessionEnd;
        }
      });
      
      // Get latest TAS score from the most recent session
      const latestSession = sessions[sessions.length - 1];
      const latestTAS = latestSession.calculationSnapshot?.currentTAS || 
                       latestSession.results?.currentTAS || 
                       latestSession.currentTAS;
      
      const org: AdminOrganization = {
        name: orgName,
        industry: sessions[0].formData?.industry || sessions[0].contactInfo?.industry,
        companySize: sessions[0].formData?.companySize || sessions[0].contactInfo?.companySize,
        totalEmployees: sessions[0].formData?.totalEmployees || sessions[0].contactInfo?.totalEmployees,
        annualHires: sessions[0].formData?.annualHires || sessions[0].contactInfo?.annualHires,
        firstAssessment,
        lastAssessment,
        totalAssessments: sessions.length,
        assessors: Array.from(assessorMap.values()),
        latestTASScore: latestTAS,
        averageTASScore: tasCount > 0 ? totalTAS / tasCount : undefined,
        bestTASScore: bestTAS > 0 ? bestTAS : undefined
      };
      
      organizations.push(org);
    }
    
    // Sort by last assessment date (handle cases where lastAssessment might be undefined)
    organizations.sort((a, b) => {
      const aTime = a.lastAssessment?.seconds || 0;
      const bTime = b.lastAssessment?.seconds || 0;
      return bTime - aTime;
    });
    
    // Apply search filter if provided
    if (filters?.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      return organizations.filter(org => 
        org.name.toLowerCase().includes(searchLower)
      );
    }
    
    return organizations;
  } catch (error) {
    return [];
  }
};

// Fetch analytics sessions (all visitors with lead source)
export const fetchAnalyticsSessions = async (): Promise<any[]> => {
  try {
    const analyticsRef = collection(db, 'calculator_analytics');
    const snapshot = await getDocs(analyticsRef);
    
    const analyticsSessions: any[] = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      // Only include sessions that have a lead source
      if (data.leadSource) {
        analyticsSessions.push({
          sessionId: doc.id,
          leadSource: data.leadSource,
          assignedConsultant: data.assignedConsultant,
          assignedConsultantName: data.assignedConsultantName,
          startedAt: data.startedAt,
          lastActivity: data.lastActivity,
          currentStep: data.currentStep || 1,
          completed: data.completed || false,
          referrer: data.referrer || '',
          isAnalyticsOnly: true, // Flag to distinguish from full sessions
          // Include tracking fields
          returnCount: data.returnCount,
          editCount: data.editCount,
          lastReturnedAt: data.lastReturnedAt,
          totalTimeSpent: data.totalTimeSpent
        });
      }
    });
    
    return analyticsSessions;
  } catch (error) {
    return [];
  }
};

// Fetch sessions with optional filters
export const fetchSessions = async (filters?: SearchFilters, includeAnalytics?: boolean): Promise<AdminSession[]> => {
  try {
    // First, fetch all reports to create a sessionId -> reportId map
    const reportsMap = new Map<string, string>();
    try {
      const reportsRef = collection(db, 'reports');
      const reportsSnapshot = await getDocs(reportsRef);
      reportsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.sessionId) {
          reportsMap.set(data.sessionId, doc.id);
        }
      });
    } catch (error) {
    }
    
    const sessionsRef = collectionGroup(db, 'calculator_sessions');
    // Remove orderBy to avoid index requirement - we'll sort in memory
    const constraints: QueryConstraint[] = [];
    
    const q = query(sessionsRef, ...constraints);
    const snapshot = await getDocs(q);
    
    const sessions: AdminSession[] = [];
    const sessionIdCounts = new Map<string, number>();

    snapshot.forEach(doc => {
      const data = doc.data();
      const sessionId = data.sessionId || doc.id;

      // Track duplicate session IDs instead of skipping them
      const count = sessionIdCounts.get(sessionId) || 0;
      sessionIdCounts.set(sessionId, count + 1);

      // Create a unique display ID if this is a duplicate
      const displaySessionId = count > 0 ? `${sessionId}_dup${count}` : sessionId;
      
      // Extract userId from the document reference path
      // Path format: users/{userId}/calculator_sessions/{sessionId}
      const pathSegments = doc.ref.path.split('/');
      const userIdFromPath = pathSegments.length > 1 ? pathSegments[1] : '';
      
      const email = data.formData?.contactEmail || data.contactInfo?.contactEmail || '';
      const isRooster = isRoosterEmail(email);
      
      // Check if this session has a report in the reports collection
      const reportIdFromMap = reportsMap.get(sessionId);
      const reportId = reportIdFromMap || data.reportId || data.latestReportId;
      
      // Simplified status determination - only 3 statuses now
      let status: AdminSession['status'] = 'in_progress';
      
      // 1. Finalized: Has a report (highest priority)
      if (reportId || data.finalizedAt || data.lastFinalizedAt || data.enhancedData) {
        status = 'finalized';
      } 
      // 2. Complete: Finished all steps but no report
      else if (data.completedAt || (data.currentStep === 4 && data.calculations)) {
        status = 'complete';
      } 
      // 3. In Progress: Default for active/incomplete sessions
      
      // Extract metrics from any session that has data (show whatever is available)
      let currentTAS: number | undefined;
      let projectedTAS: number | undefined;
      let totalAnnualSavings: number | undefined;
      let year1Savings: number | undefined;
      let steadyStateSavings: number | undefined;
      
      // Always try to extract metrics if they exist, regardless of status
      if (data.calculations || data.results || data.calculationSnapshot) {
        currentTAS = data.calculationSnapshot?.currentTAS || 
                    data.results?.currentTAS || 
                    data.calculations?.currentTAS ||  // Check calculations too
                    data.currentTAS;
        
        projectedTAS = data.calculationSnapshot?.projectedTAS || 
                      data.results?.projectedTAS || 
                      data.calculations?.projectedTAS ||  // Check calculations too
                      data.projectedTAS;
        
        // Extract enhanced savings if available
        const enhancedSavings = data.results?.enhancedSavings || 
                                data.calculations?.enhancedSavings ||  // Check calculations too
                                data.enhancedData?.enhancedSavings;
        if (enhancedSavings) {
          // year1Savings is an object with totalAnnualSavings property
          year1Savings = enhancedSavings.year1Savings?.totalAnnualSavings;
          // steadyStateSavings is also an object with totalAnnualSavings property
          steadyStateSavings = enhancedSavings.steadyStateSavings?.totalAnnualSavings;
          // Use steady state as the primary total if available
          totalAnnualSavings = steadyStateSavings || enhancedSavings.totalAnnualSavings;
        } else {
          totalAnnualSavings = data.calculationSnapshot?.totalAnnualSavings || 
                              data.results?.savings?.totalAnnualSavings ||
                              data.calculations?.savings?.totalAnnualSavings;  // Check calculations too
        }
      }
      
      // Also extract ROI percentages if available
      const year1ROI = data.results?.year1ROI || 
                      data.calculations?.year1ROI ||
                      data.year1ROI;
      const steadyStateROI = data.results?.steadyStateROI || 
                           data.calculations?.steadyStateROI ||
                           data.steadyStateROI;
      
      // Extract payback month from payback metrics
      const paybackMonth = data.results?.paybackMetrics?.paybackMonth ||
                          data.calculations?.paybackMetrics?.paybackMonth;

      const derivedStep = data.currentStep || (
        status === 'finalized' ?
          (data.results?.enhancedSavings || data.enhancedData || data.year1ROI ? 4 : 3) :
          status === 'complete' ? 3 :
          undefined
      );

      const session: AdminSession = {
        sessionId: displaySessionId,  // Use the unique display ID
        userId: data.userId || userIdFromPath || '',
        isDuplicate: count > 0,  // Flag if this is a duplicate
        userEmail: email,
        userName: data.formData?.contactName || data.contactInfo?.contactName || '',
        organizationName: data.formData?.organizationName || data.contactInfo?.organizationName || '',
        assessmentType: isRooster ? 'consultant' : 'self',
        isRoosterAssessment: isRooster,
        onBehalfOf: isRooster ? (data.formData?.organizationName || data.contactInfo?.organizationName) : undefined,
        leadSource: data.leadSource,
        assignedConsultant: data.assignedConsultant,
        assignedConsultantName: data.assignedConsultantName,
        calculatorPath: data.formData?.calculatorPath,
        isEditSession: data.isEditSession,
        originalReportId: data.originalReportId,
        originalSessionId: data.originalSessionId,
        startedAt: data.startedAt || data.savedAt || data.createdAt || data.updatedAt || null,
        completedAt: data.completedAt,
        finalizedAt: data.finalizedAt || data.lastFinalizedAt,
        currentTAS: currentTAS,
        projectedTAS: projectedTAS,
        totalAnnualSavings: totalAnnualSavings,
        year1Savings: year1Savings,
        steadyStateSavings: steadyStateSavings,
        year1ROI: year1ROI,
        steadyStateROI: steadyStateROI,
        paybackMonth: paybackMonth,
        reportGenerated: !!reportId || !!data.enhancedData || !!data.finalizedAt || !!data.lastFinalizedAt,
        reportId: reportId,  // Use the reportId we found from cross-reference
        emailsSent: data.emailRecipients?.length || 0,
        emailRecipients: data.emailRecipients || [],
        formData: data.formData || {},
        results: data.results || data.calculations || {},
        status: status,
        currentStep: derivedStep,
        highestStepReached: data.highestStepReached || derivedStep,
        // Tracking fields
        returnCount: data.returnCount,
        editCount: data.editCount,
        reportViewCount: data.reportViewCount,
        uniqueViewers: data.uniqueViewers,
        lastReturnedAt: data.lastReturnedAt,
        totalTimeSpent: data.totalTimeSpent
      };
      
      // Apply filters
      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();

        // Check if search term matches any email recipient
        const matchesEmailRecipient = session.emailRecipients?.some(email =>
          email.toLowerCase().includes(searchLower)
        );

        if (!session.userEmail.toLowerCase().includes(searchLower) &&
            !session.userName.toLowerCase().includes(searchLower) &&
            !session.organizationName.toLowerCase().includes(searchLower) &&
            !session.sessionId.toLowerCase().includes(searchLower) &&
            !matchesEmailRecipient) {
          return;
        }
      }
      
      if (filters?.assessmentType && filters.assessmentType !== 'all') {
        if (session.assessmentType !== filters.assessmentType) {
          return;
        }
      }
      
      if (filters?.hasReport !== undefined) {
        if (session.reportGenerated !== filters.hasReport) {
          return;
        }
      }
      
      if (filters?.leadSource) {
        if (session.leadSource !== filters.leadSource) {
          return;
        }
      }
      
      sessions.push(session);
    });
    
    // If includeAnalytics is true, also fetch and merge analytics sessions
    if (includeAnalytics) {
      const analyticsSessions = await fetchAnalyticsSessions();
      
      // Create a map of existing session IDs to avoid duplicates
      const existingSessionIds = new Set(sessions.map(s => s.sessionId));
      
      // Add analytics-only sessions (visitors who haven't provided email yet)
      for (const analyticsSession of analyticsSessions) {
        // Skip if this analytics session doesn't match the requested lead source filter
        if (filters?.leadSource && analyticsSession.leadSource !== filters.leadSource) {
          continue;
        }

        if (!existingSessionIds.has(analyticsSession.sessionId)) {
          // Create a minimal AdminSession for analytics-only entries
          const minimalSession: AdminSession = {
            sessionId: analyticsSession.sessionId,
            userId: '',
            userEmail: 'No email provided',
            userName: 'Anonymous Visitor',
            organizationName: 'Unknown',
            assessmentType: 'self',
            isRoosterAssessment: false,
            leadSource: analyticsSession.leadSource,
            assignedConsultant: analyticsSession.assignedConsultant,
            assignedConsultantName: analyticsSession.assignedConsultantName,
            startedAt: analyticsSession.startedAt,
            status: analyticsSession.completed ? 'complete' : 'in_progress',
            currentStep: analyticsSession.currentStep,
            highestStepReached: analyticsSession.currentStep,
            reportGenerated: false,
            emailsSent: 0,
            formData: {} as CalculatorFormData,  // Analytics sessions don't have form data
            results: {} as CalculatorResults,    // Analytics sessions don't have results
            // Tracking fields from analytics
            returnCount: (analyticsSession as any).returnCount,
            editCount: (analyticsSession as any).editCount,
            lastReturnedAt: (analyticsSession as any).lastReturnedAt,
            totalTimeSpent: (analyticsSession as any).totalTimeSpent
          };
          sessions.push(minimalSession);
        } else {
          // Update existing session with lead source if it's missing
          const existingSession = sessions.find(s => s.sessionId === analyticsSession.sessionId);
          if (existingSession && !existingSession.leadSource && analyticsSession.leadSource) {
            existingSession.leadSource = analyticsSession.leadSource;
          }
        }
      }
    }
    
    // Sort sessions by completedAt or startedAt in descending order
    sessions.sort((a, b) => {
      const dateA = (a.completedAt || a.startedAt)?.seconds || 0;
      const dateB = (b.completedAt || b.startedAt)?.seconds || 0;
      return dateB - dateA;
    });
    
    return sessions;
  } catch (error) {
    return [];
  }
};

// Fetch reports
export const fetchReports = async (filters?: SearchFilters): Promise<AdminReport[]> => {
  try {
    const reportsRef = collection(db, 'reports');
    // Remove orderBy to avoid index requirement - we'll sort in memory
    const constraints: QueryConstraint[] = [];
    
    const q = query(reportsRef, ...constraints);
    let snapshot;
    
    try {
      snapshot = await getDocs(q);
    } catch (error) {
      // Reports collection might not exist yet or have permission issues
      return [];
    }
    
    const reports: AdminReport[] = [];
    const seenReportIds = new Set<string>();
    
    for (const docSnap of snapshot.docs) {
      const reportId = docSnap.id;
      
      // Skip duplicate reports
      if (seenReportIds.has(reportId)) {
        continue;
      }
      seenReportIds.add(reportId);
      
      const data = docSnap.data();
      
      // Try to get visit count, but don't fail if we can't access it
      let visitCount = 0;
      try {
        const visitsRef = collection(db, 'report_visits');
        const visitsQuery = query(visitsRef, where('reportId', '==', docSnap.id));
        const visitsSnapshot = await getDocs(visitsQuery);
        visitCount = visitsSnapshot.size;
      } catch (error) {
        // Silently ignore if we can't access visit counts
        // This is expected due to Firestore permissions
      }
      
      // Extract metrics from various possible locations
      const currentTAS = data.results?.currentTAS || 
                        data.calculationSnapshot?.currentTAS || 
                        data.currentTAS || 0;
      
      const projectedTAS = data.results?.projectedTAS || 
                          data.calculationSnapshot?.projectedTAS || 
                          data.projectedTAS || 0;
      
      const totalAnnualSavings = data.results?.savings?.totalAnnualSavings || 
                                data.calculationSnapshot?.totalAnnualSavings ||
                                data.enhancedData?.enhancedSavings?.totalAnnualSavings || 0;
      
      const year1ROI = data.enhancedData?.year1ROI || 
                      data.calculationSnapshot?.year1ROI;
      
      const report: AdminReport = {
        reportId: reportId,
        sessionId: data.sessionId || '',
        userId: data.userId || '',
        userEmail: data.formData?.contactEmail || data.contactInfo?.contactEmail || '',
        userName: data.formData?.contactName || data.contactInfo?.contactName || '',
        organizationName: data.formData?.organizationName || data.contactInfo?.organizationName || '',
        createdBy: data.formData?.contactName || data.contactInfo?.contactName || '',
        createdAt: data.finalizedAt || Timestamp.now(),
        accessCount: visitCount,
        lastAccessed: data.lastAccessed,
        emailRecipients: data.metadata?.emailRecipients || [],
        currentTAS: currentTAS,
        projectedTAS: projectedTAS,
        totalAnnualSavings: totalAnnualSavings,
        year1ROI: year1ROI,
        scenarioMode: data.enhancedData?.scenarioMode || data.scenarioMode
      };
      
      // Apply filters
      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();

        // Check if search term matches any email recipient
        const matchesEmailRecipient = report.emailRecipients?.some(email =>
          email.toLowerCase().includes(searchLower)
        );

        if (!report.userEmail.toLowerCase().includes(searchLower) &&
            !report.userName.toLowerCase().includes(searchLower) &&
            !report.organizationName.toLowerCase().includes(searchLower) &&
            !report.reportId.toLowerCase().includes(searchLower) &&
            !matchesEmailRecipient) {
          continue;
        }
      }
      
      reports.push(report);
    }
    
    // Sort reports by createdAt in descending order
    reports.sort((a, b) => {
      const dateA = a.createdAt?.seconds || 0;
      const dateB = b.createdAt?.seconds || 0;
      return dateB - dateA;
    });
    
    return reports;
  } catch (error) {
    return [];
  }
};

// Get admin statistics
export const getAdminStats = async (): Promise<AdminStats> => {
  try {
    // Fetch all data for statistics
    const users = await fetchUsers();
    const organizations = await fetchOrganizations();
    const sessions = await fetchSessions();
    const reports = await fetchReports();
    
    // Calculate statistics
    const roosterCount = users.filter(u => u.isRooster).length;
    const completedSessions = sessions.filter(s => s.completedAt);
    const consultantAssessments = sessions.filter(s => s.assessmentType === 'consultant');
    const selfAssessments = sessions.filter(s => s.assessmentType === 'self');
    
    // Calculate average TAS
    let totalTAS = 0;
    let tasCount = 0;
    sessions.forEach(s => {
      if (s.currentTAS) {
        totalTAS += s.currentTAS;
        tasCount++;
      }
    });
    
    // Calculate average ROI
    let totalROI = 0;
    let roiCount = 0;
    reports.forEach(r => {
      if (r.year1ROI) {
        totalROI += r.year1ROI;
        roiCount++;
      }
    });
    
    return {
      totalUsers: users.length,
      totalOrganizations: organizations.length,
      totalSessions: sessions.length,
      totalReports: reports.length,
      averageTASScore: tasCount > 0 ? totalTAS / tasCount : 0,
      averageROI: roiCount > 0 ? totalROI / roiCount : 0,
      completionRate: sessions.length > 0 ? (completedSessions.length / sessions.length) * 100 : 0,
      roosterCount,
      clientAssessmentCount: selfAssessments.length,
      consultantAssessmentCount: consultantAssessments.length
    };
  } catch (error) {
    return {
      totalUsers: 0,
      totalOrganizations: 0,
      totalSessions: 0,
      totalReports: 0,
      averageTASScore: 0,
      averageROI: 0,
      completionRate: 0,
      roosterCount: 0,
      clientAssessmentCount: 0,
      consultantAssessmentCount: 0
    };
  }
};

// Search across users and organizations
export const searchAll = async (searchTerm: string): Promise<{
  users: AdminUser[];
  organizations: AdminOrganization[];
}> => {
  const [users, organizations] = await Promise.all([
    fetchUsers({ searchTerm }),
    fetchOrganizations({ searchTerm })
  ]);
  
  return { users, organizations };
};

// Fetch recent activity (sessions) for activity feed
export const fetchSessionByReportId = async (reportId: string): Promise<AdminSession | null> => {
  try {
    const sessions = await fetchSessions();
    // Find the session with the matching reportId
    const session = sessions.find(s => s.reportId === reportId);
    return session || null;
  } catch (error) {
    console.error('Error fetching session by report ID:', error);
    return null;
  }
};

// Fetch a session by its session ID
export const fetchSessionBySessionId = async (sessionId: string): Promise<AdminSession | null> => {
  try {
    const sessions = await fetchSessions(undefined, true); // Include analytics to get all sessions
    // Find the session with the matching sessionId
    const session = sessions.find(s => s.sessionId === sessionId);
    return session || null;
  } catch (error) {
    console.error('Error fetching session by session ID:', error);
    return null;
  }
};

export const fetchRecentActivity = async (limit: number = 50): Promise<AdminSession[]> => {
  try {
    // Include analytics sessions to merge lead sources onto known users
    const sessions = await fetchSessions(undefined, true);
    // Filter out anonymous visitors - they're shown in Lead Sources view instead
    const knownUserSessions = sessions.filter(s => s.userEmail !== 'No email provided');
    // Already sorted by date in fetchSessions, just limit the results
    return knownUserSessions.slice(0, limit);
  } catch (error) {
    return [];
  }
};

// Fetch sessions for a specific person at a specific organization
export const fetchPersonOrgSessions = async (
  userEmail: string, 
  organizationName: string
): Promise<AdminSession[]> => {
  try {
    // Include analytics sessions to show all visitors with lead sources
    const sessions = await fetchSessions(undefined, true);
    return sessions.filter(s => 
      s.userEmail.toLowerCase() === userEmail.toLowerCase() &&
      s.organizationName.toLowerCase() === organizationName.toLowerCase()
    );
  } catch (error) {
    return [];
  }
};

// Fetch all organizations a person has assessed
export const fetchPersonOrganizations = async (userEmail: string): Promise<string[]> => {
  try {
    const sessions = await fetchSessions();
    const orgs = new Set<string>();
    sessions
      .filter(s => s.userEmail.toLowerCase() === userEmail.toLowerCase())
      .forEach(s => orgs.add(s.organizationName));
    return Array.from(orgs);
  } catch (error) {
    return [];
  }
};

// Fetch all people who have assessed an organization
export const fetchOrganizationAssessors = async (organizationName: string): Promise<{
  email: string;
  name: string;
  sessionCount: number;
  lastAssessment: Timestamp;
}[]> => {
  try {
    const sessions = await fetchSessions();
    const assessorMap = new Map<string, any>();
    
    sessions
      .filter(s => s.organizationName.toLowerCase() === organizationName.toLowerCase())
      .forEach(s => {
        if (!assessorMap.has(s.userEmail)) {
          assessorMap.set(s.userEmail, {
            email: s.userEmail,
            name: s.userName,
            sessionCount: 0,
            lastAssessment: s.completedAt || s.startedAt
          });
        }
        const assessor = assessorMap.get(s.userEmail);
        assessor.sessionCount++;
        if (s.completedAt && s.completedAt > assessor.lastAssessment) {
          assessor.lastAssessment = s.completedAt;
        }
      });
    
    return Array.from(assessorMap.values());
  } catch (error) {
    return [];
  }
};

// Dashboard metrics function
export const fetchDashboardMetrics = async () => {
  try {
    const [sessions, users, organizations] = await Promise.all([
      fetchSessions(),
      fetchPersons(),
      fetchOrganizations()
    ]);

    // Calculate date ranges
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    // Filter sessions by month
    const sessionsThisMonth = sessions.filter(s => {
      const date = s.startedAt instanceof Date ? s.startedAt : s.startedAt.toDate();
      return date >= thisMonthStart;
    }).length;

    const sessionsLastMonth = sessions.filter(s => {
      const date = s.startedAt instanceof Date ? s.startedAt : s.startedAt.toDate();
      return date >= lastMonthStart && date <= lastMonthEnd;
    }).length;

    // Calculate average TAS from finalized sessions
    const finalizedSessions = sessions.filter(s => s.status === 'finalized' && s.currentTAS);
    const averageTAS = finalizedSessions.length > 0
      ? finalizedSessions.reduce((sum, s) => sum + (s.currentTAS || 0), 0) / finalizedSessions.length
      : 0;

    return {
      totalSessions: sessions.length,
      totalUsers: users.length,
      totalOrganizations: organizations.length,
      averageTAS,
      sessionsThisMonth,
      sessionsLastMonth
    };
  } catch (error) {
    throw error;
  }
};

// Helper to create userId from email (matching firestoreService.ts logic)
const createUserIdFromEmail = (email: string): string => {
  // This should match the logic in firestoreService.ts createUserDocumentId function
  return email.replace(/[.#$[\]]/g, '_').toLowerCase();
};

// Delete a session and all associated data
export const deleteSession = async (session: AdminSession): Promise<{
  success: boolean;
  deletedItems: {
    analytics: boolean;
    userSession: boolean;
    reports: number;
    reportVisits: number;
    finalizedReports: number;
  };
  errors: string[];
}> => {
  const deletedItems = {
    analytics: false,
    userSession: false,
    reports: 0,
    reportVisits: 0,
    finalizedReports: 0
  };
  const errors: string[] = [];


  try {
    // 1. Delete from calculator_analytics collection
    try {
      await deleteDoc(doc(db, 'calculator_analytics', session.sessionId));
      deletedItems.analytics = true;
    } catch (error: any) {
      // Document might not exist in analytics collection - that's ok
    }

    // 2. Delete from users/{userId}/calculator_sessions/{sessionId}
    // Try with provided userId first, then fallback to email-based userId
    let userIdToUse = session.userId;

    // If no userId or userId seems invalid, try to generate it from email
    if (!userIdToUse && session.userEmail) {
      userIdToUse = createUserIdFromEmail(session.userEmail);
    }

    if (userIdToUse) {
      // First try to delete using sessionId as document ID (normal case)
      let deleted = false;

      try {
        const sessionRef = doc(db, 'users', userIdToUse, 'calculator_sessions', session.sessionId);

        // Check if the document exists
        const sessionDoc = await getDoc(sessionRef);
        if (sessionDoc.exists()) {
          // First, delete all finalized_reports in the subcollection
          try {
            const reportsRef = collection(db, 'users', userIdToUse, 'calculator_sessions', session.sessionId, 'finalized_reports');
            const reportsSnapshot = await getDocs(reportsRef);

            for (const reportDoc of reportsSnapshot.docs) {
              await deleteDoc(reportDoc.ref);
              deletedItems.finalizedReports++;
            }
          } catch (error: any) {
            // Silent failure for finalized reports cleanup
          }

          // Then delete the session document itself
          await deleteDoc(sessionRef);
          deletedItems.userSession = true;
          deleted = true;
        }
      } catch (error: any) {
        // Document might not exist with sessionId as document ID
      }

      // If not deleted, search for document by sessionId field value (handles mismatch cases)
      if (!deleted) {
        try {
          const sessionsRef = collection(db, 'users', userIdToUse, 'calculator_sessions');
          const q = query(sessionsRef, where('sessionId', '==', session.sessionId));
          const snapshot = await getDocs(q);

          for (const docSnap of snapshot.docs) {
            // Delete finalized_reports subcollection
            try {
              const reportsRef = collection(docSnap.ref, 'finalized_reports');
              const reportsSnapshot = await getDocs(reportsRef);

              for (const reportDoc of reportsSnapshot.docs) {
                await deleteDoc(reportDoc.ref);
                deletedItems.finalizedReports++;
              }
            } catch (error: any) {
              // Silent failure for finalized reports cleanup
            }

            // Delete the session document
            await deleteDoc(docSnap.ref);
            deletedItems.userSession = true;
            deleted = true;
          }
        } catch (error: any) {
          console.error('Failed to find/delete user session by query:', error.message);
          errors.push(`Failed to delete user session: ${error.message}`);
        }
      }

      if (!deleted) {
        errors.push(`Session document not found for deletion: ${session.sessionId}`);
      }
    } else {
      errors.push('No userId available to delete user session');
    }

    // 3. Delete from reports collection if report exists
    if (session.reportId) {
      try {
        await deleteDoc(doc(db, 'reports', session.reportId));
        deletedItems.reports++;
      } catch (error: any) {
        // Silent failure for report document cleanup
      }
    }

    // 4. Delete related report_visits entries
    if (session.reportId) {
      try {
        const visitsQuery = query(
          collection(db, 'report_visits'),
          where('reportId', '==', session.reportId)
        );
        const visitsSnapshot = await getDocs(visitsQuery);
        
        for (const visitDoc of visitsSnapshot.docs) {
          await deleteDoc(visitDoc.ref);
          deletedItems.reportVisits++;
        }
      } catch (error: any) {
        // Silent failure for report visits cleanup
      }
    }

    // Return success if at least one main item was deleted
    const success = deletedItems.analytics || deletedItems.userSession || deletedItems.reports > 0;

    return {
      success,
      deletedItems,
      errors
    };
  } catch (error: any) {
    console.error('Critical error during delete:', error);
    return {
      success: false,
      deletedItems,
      errors: [`Critical error: ${error.message}`]
    };
  }
};

// Fetch all unique lead sources from existing sessions
export const fetchUniqueLeadSources = async (): Promise<string[]> => {
  try {
    const leadSources = new Set<string>();
    
    // Fetch from calculator_analytics collection
    try {
      const analyticsRef = collection(db, 'calculator_analytics');
      const analyticsSnapshot = await getDocs(analyticsRef);
      analyticsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.leadSource && typeof data.leadSource === 'string' && data.leadSource.trim()) {
          leadSources.add(data.leadSource.trim());
        }
      });
    } catch (error) {
      console.error('Error fetching lead sources from analytics:', error);
    }
    
    // Fetch from user sessions subcollections using collectionGroup
    try {
      const sessionsRef = collectionGroup(db, 'calculator_sessions');
      const sessionsSnapshot = await getDocs(sessionsRef);
      sessionsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.leadSource && typeof data.leadSource === 'string' && data.leadSource.trim()) {
          leadSources.add(data.leadSource.trim());
        }
      });
    } catch (error) {
      console.error('Error fetching lead sources from user sessions:', error);
    }
    
    // Convert to array and sort alphabetically
    return Array.from(leadSources).sort((a, b) => 
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  } catch (error) {
    console.error('Error fetching unique lead sources:', error);
    return [];
  }
};

// Update lead source for a session
export const updateSessionLeadSource = async (
  session: AdminSession, 
  leadSource: string | null
): Promise<{
  success: boolean;
  updatedLocations: {
    analytics: boolean;
    userSession: boolean;
  };
  errors: string[];
}> => {
  const updatedLocations = {
    analytics: false,
    userSession: false
  };
  const errors: string[] = [];


  try {
    // 1. Update in calculator_analytics collection
    try {
      const analyticsRef = doc(db, 'calculator_analytics', session.sessionId);
      if (leadSource) {
        await updateDoc(analyticsRef, { leadSource });
      } else {
        // Remove lead source if null
        await updateDoc(analyticsRef, { leadSource: '' });
      }
      updatedLocations.analytics = true;
    } catch (error: any) {
      // Document might not exist in analytics collection - that's ok
    }

    // 2. Update in users/{userId}/calculator_sessions/{sessionId}
    let userIdToUse = session.userId;
    
    // If no userId, try to generate it from email
    if (!userIdToUse && session.userEmail) {
      userIdToUse = createUserIdFromEmail(session.userEmail);
    }
    
    if (userIdToUse) {
      try {
        const sessionRef = doc(db, 'users', userIdToUse, 'calculator_sessions', session.sessionId);
        if (leadSource) {
          await updateDoc(sessionRef, { leadSource });
        } else {
          // Remove lead source if null
          await updateDoc(sessionRef, { leadSource: '' });
        }
        updatedLocations.userSession = true;
      } catch (error: any) {
        console.error('Failed to update user session:', error.message);
        errors.push(`Failed to update user session: ${error.message}`);
      }
    } else {
      errors.push('No userId available to update user session');
    }

    // Return success if at least one location was updated
    const success = updatedLocations.analytics || updatedLocations.userSession;


    return {
      success,
      updatedLocations,
      errors
    };
  } catch (error: any) {
    console.error('Critical error during lead source update:', error);
    return {
      success: false,
      updatedLocations,
      errors: [`Critical error: ${error.message}`]
    };
  }
};

// Predefined Rooster consultants (starts empty, populated from database)
export const PREDEFINED_CONSULTANTS: ConsultantInfo[] = [];

// Fetch all unique consultants from existing sessions
export const fetchUniqueConsultants = async (): Promise<ConsultantInfo[]> => {
  try {
    const consultantMap = new Map<string, ConsultantInfo>();

    // Add predefined consultants
    PREDEFINED_CONSULTANTS.forEach(consultant => {
      consultantMap.set(consultant.email.toLowerCase(), consultant);
    });

    // Fetch from calculator_analytics collection
    try {
      const analyticsRef = collection(db, 'calculator_analytics');
      const analyticsSnapshot = await getDocs(analyticsRef);
      analyticsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.assignedConsultant && typeof data.assignedConsultant === 'string' && data.assignedConsultant.trim()) {
          const email = data.assignedConsultant.trim().toLowerCase();
          if (email.endsWith('@roosterinc.com') && !consultantMap.has(email)) {
            // Extract name from email if not in predefined list
            const name = data.assignedConsultantName || email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            consultantMap.set(email, { name, email: data.assignedConsultant.trim() });
          }
        }
      });
    } catch (error) {
      console.error('Error fetching consultants from analytics:', error);
    }

    // Fetch from user sessions subcollections using collectionGroup
    try {
      const sessionsRef = collectionGroup(db, 'calculator_sessions');
      const sessionsSnapshot = await getDocs(sessionsRef);
      sessionsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.assignedConsultant && typeof data.assignedConsultant === 'string' && data.assignedConsultant.trim()) {
          const email = data.assignedConsultant.trim().toLowerCase();
          if (email.endsWith('@roosterinc.com') && !consultantMap.has(email)) {
            // Extract name from email if not in predefined list
            const name = data.assignedConsultantName || email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            consultantMap.set(email, { name, email: data.assignedConsultant.trim() });
          }
        }
      });
    } catch (error) {
      console.error('Error fetching consultants from user sessions:', error);
    }

    // Return sorted array of consultants
    return Array.from(consultantMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Error fetching unique consultants:', error);
    return PREDEFINED_CONSULTANTS;
  }
};

// Fetch Rooster team members (returns predefined list) - kept for backwards compatibility
export const fetchRoosterTeamMembers = async (): Promise<string[]> => {
  const consultants = await fetchUniqueConsultants();
  return consultants.map(c => c.name);
};

// Update session with assigned consultant
export const updateSessionConsultant = async (
  session: AdminSession,
  assignedConsultant: string | null,
  assignedConsultantName?: string | null
): Promise<{
  success: boolean;
  updatedLocations: {
    analytics: boolean;
    userSession: boolean;
  };
  errors: string[];
}> => {
  const updatedLocations = {
    analytics: false,
    userSession: false
  };
  const errors: string[] = [];

  try {
    // 1. Update in calculator_analytics collection
    try {
      const analyticsRef = doc(db, 'calculator_analytics', session.sessionId);
      if (assignedConsultant) {
        const updateData: any = { assignedConsultant };
        if (assignedConsultantName) {
          updateData.assignedConsultantName = assignedConsultantName;
        }
        await updateDoc(analyticsRef, updateData);
      } else {
        // Remove consultant if null
        await updateDoc(analyticsRef, { assignedConsultant: '', assignedConsultantName: '' });
      }
      updatedLocations.analytics = true;
    } catch (error: any) {
      // Document might not exist in analytics collection - that's ok
    }

    // 2. Update in users/{userId}/calculator_sessions/{sessionId}
    let userIdToUse = session.userId;

    // If no userId, try to generate it from email
    if (!userIdToUse && session.userEmail) {
      userIdToUse = createUserIdFromEmail(session.userEmail);
    }

    if (userIdToUse) {
      try {
        const sessionRef = doc(db, 'users', userIdToUse, 'calculator_sessions', session.sessionId);
        if (assignedConsultant) {
          const updateData: any = { assignedConsultant };
          if (assignedConsultantName) {
            updateData.assignedConsultantName = assignedConsultantName;
          }
          await updateDoc(sessionRef, updateData);
        } else {
          // Remove consultant if null
          await updateDoc(sessionRef, { assignedConsultant: '', assignedConsultantName: '' });
        }
        updatedLocations.userSession = true;
      } catch (error: any) {
        console.error('Failed to update user session:', error.message);
        errors.push(`Failed to update user session: ${error.message}`);
      }
    } else {
      errors.push('No userId available to update user session');
    }

    // Return success if at least one location was updated
    const success = updatedLocations.analytics || updatedLocations.userSession;

    return {
      success,
      updatedLocations,
      errors
    };
  } catch (error: any) {
    console.error('Critical error during consultant update:', error);
    return {
      success: false,
      updatedLocations,
      errors: [`Critical error: ${error.message}`]
    };
  }
};

/**
 * Fix missing currentStep for historical sessions
 * This function will analyze the data and determine the correct step
 */
export const fixMissingCurrentStep = async (sessionId: string, userId: string): Promise<void> => {
  try {
    const sessionRef = doc(db, 'users', userId, 'calculator_sessions', sessionId);
    const sessionDoc = await getDoc(sessionRef);

    if (!sessionDoc.exists()) {
      console.error(`[FixCurrentStep] Session ${sessionId} not found for user ${userId}`);
      return;
    }

    const data = sessionDoc.data();

    // Skip if currentStep already exists
    if (data.currentStep) {
      return;
    }

    // Determine the correct step based on data
    let derivedStep: number | undefined;

    if (data.finalizedAt || data.reportId) {
      // Finalized sessions - check if they have ROI data
      if (data.results?.enhancedSavings || data.enhancedData || data.year1ROI) {
        derivedStep = 4; // ROI Analysis completed
      } else {
        derivedStep = 3; // Only TAS calculation completed
      }
    } else if (data.completedAt) {
      derivedStep = 3; // Completed but not finalized
    } else if (data.formData) {
      // Check what form data we have to determine step
      if (data.formData.industryAverageTAS !== undefined) {
        derivedStep = 3; // At least reached step 3
      } else if (data.formData.totalInterviews !== undefined) {
        derivedStep = 2; // At least reached step 2
      } else if (data.formData.contactEmail) {
        derivedStep = 1; // At least started step 1
      }
    }

    if (derivedStep) {
      await updateDoc(sessionRef, {
        currentStep: derivedStep,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error(`[FixCurrentStep] Error fixing session ${sessionId}:`, error);
  }
};

/**
 * Delete all sessions with a specific lead source
 * @param leadSource The lead source to delete
 * @returns Object with deletion results
 */
export const deleteLeadSourceSessions = async (leadSource: string): Promise<{
  success: boolean;
  deletedAnalytics: number;
  updatedSessions: number;
  errors: string[];
}> => {
  const errors: string[] = [];
  let deletedAnalytics = 0;
  let updatedSessions = 0;

  try {
    // 1. Delete from calculator_analytics collection where leadSource matches
    try {
      const analyticsRef = collection(db, 'calculator_analytics');
      const analyticsSnapshot = await getDocs(analyticsRef);

      for (const doc of analyticsSnapshot.docs) {
        const data = doc.data();
        if (data.leadSource === leadSource) {
          try {
            await deleteDoc(doc.ref);
            deletedAnalytics++;
          } catch (error: any) {
            errors.push(`Failed to delete analytics session ${doc.id}: ${error.message}`);
          }
        }
      }
    } catch (error: any) {
      errors.push(`Failed to access analytics collection: ${error.message}`);
    }

    // 2. Update all user sessions to remove this lead source
    try {
      const sessionsRef = collectionGroup(db, 'calculator_sessions');
      const sessionsSnapshot = await getDocs(sessionsRef);

      for (const doc of sessionsSnapshot.docs) {
        const data = doc.data();
        if (data.leadSource === leadSource) {
          try {
            // Clear the lead source from this session
            await updateDoc(doc.ref, { leadSource: '' });
            updatedSessions++;
          } catch (error: any) {
            errors.push(`Failed to update session ${doc.id}: ${error.message}`);
          }
        }
      }
    } catch (error: any) {
      errors.push(`Failed to access user sessions: ${error.message}`);
    }

    return {
      success: deletedAnalytics > 0 || updatedSessions > 0,
      deletedAnalytics,
      updatedSessions,
      errors
    };
  } catch (error: any) {
    return {
      success: false,
      deletedAnalytics,
      updatedSessions,
      errors: [`Critical error: ${error.message}`]
    };
  }
};

/**
 * Batch fix all sessions missing currentStep
 */
export const batchFixMissingCurrentStep = async (): Promise<{ fixed: number; skipped: number; errors: number }> => {
  try {
    const stats = { fixed: 0, skipped: 0, errors: 0 };

    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));

    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;

      // Get all sessions for this user
      const sessionsSnapshot = await getDocs(
        collection(db, 'users', userId, 'calculator_sessions')
      );

      for (const sessionDoc of sessionsSnapshot.docs) {
        const sessionId = sessionDoc.id;
        const data = sessionDoc.data();

        if (!data.currentStep) {
          try {
            await fixMissingCurrentStep(sessionId, userId);
            stats.fixed++;
          } catch (error) {
            console.error(`Error fixing session ${sessionId}:`, error);
            stats.errors++;
          }
        } else {
          stats.skipped++;
        }
      }
    }

    return stats;
  } catch (error) {
    console.error('[BatchFix] Error:', error);
    throw error;
  }
};